/** @typedef {import('react')} JSX */
import React, { useEffect, useState, type JSX } from 'react';
import styles from './Icon.module.scss';
import { getFileIcons } from './fileIcons';

/**
 * IconProps defines the properties for the Icon component.
 */
export interface IconProps {
  type: string;
  name: string;
  size: string;
  variant: string;
  shape: string;
}

const imageModules = import.meta.glob(
  '../../../assets/Icons/Library/*.{png,jpg,jpeg,svg}',
  {
    eager: true,
    import: 'default',
  }
);

const getBaseIcons = () => {
  return Object.entries(imageModules)
    .filter(([path]) => /_(filled|outline)\.\w+$/.test(path))
    .map(([path, mod]: [string, unknown]) => {
      const fileName = path.replace(/^.*[\\/]/, '').replace(/\.\w+$/, '');
      const variant = fileName.endsWith('_filled') ? 'filled' : 'outline';
      const name = path
        .replace(/^.*[\\/]/, '')
        .replace(/\.\w+$/, '')
        .replace(/_(filled|outline)$/, '');
      return { name, variant, path: mod as string };
    });
};
/**
 * Custom Icon component for displaying different icon types such as Base, File, and Social Media. It supports variant, size, and shape customization.
 *
 * ## Import path
 * ```tsx
 * import { Icon } from '@kpmg-global-audit-emu/react-ui-kit/atoms/Icon';
 * ```
 * @param {IconProps} props - Props including type, name, size, variant, and shape.
 * @returns {JSX.Element} Rendered icon component.
 */
export const Icon: React.FC<IconProps> & {
  getAvailableNames: (type: string) => string[];
} = ({ type, name, size, variant, shape }): JSX.Element => {
  const [iconSrc, setIconSrc] = useState<string | null>(null);

  useEffect(() => {
    const icons = getFileIcons();

    if (type === 'Base Icon') {
      const iconVariant = variant?.toLowerCase?.() === 'filled' ? 'filled' : 'outline';
      const icons = getBaseIcons();
      const found =
        icons.find((icon) => icon.name === name && icon.variant === iconVariant) ??
        icons.find((icon) => icon.name === name && icon.variant === 'outline') ??
        icons.find((icon) => icon.name === name);
      setIconSrc(found?.path ?? null);
    } else if (type === 'File' || type === 'Social Media Icon') {
      setIconSrc(icons[name] || null);
    } else {
      setIconSrc(null);
    }
  }, [type, name, variant]);

  let sizeClass = '';
  if (size === 'x-small') {
    sizeClass = 'xsmall';
  } else if (size === 'xx-small') {
    sizeClass = 'xxsmall';
  } else {
    sizeClass = size?.toLowerCase?.() ?? '';
  }

  const iconClass = [
    styles.icon,
    styles[type.replace(/\s+/g, '').toLowerCase()],
    sizeClass ? styles[sizeClass.toLowerCase()] : '',
    variant ? styles[variant.toLowerCase()] : '',
    shape ? styles[shape.toLowerCase()] : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles[`icon-container`]}>
      {/* <img src={iconSrc ?? ''} alt={name} className={iconClass} /> */}
      {iconSrc && <img src={iconSrc} alt={name} className={iconClass} />}
      {name === 'IconIndicator' && <span className="indicator_span">9+</span>}
    </div>
  );
};

Icon.getAvailableNames = (type: string) => {
  switch (type) {
    case 'Base Icon':
      return [...new Set(getBaseIcons().map((icon) => icon.name))];
    case 'File':
      return [
        'CSV',
        'PDF',
        'DOCX',
        'DOC',
        'Xls',
        'TXT',
        'PPT',
        'ZIP',
        'RAR',
        'SVG',
        'PNG',
        'GIF',
        'MP4',
        'MP3',
        'JAVA',
        'XML',
        'HTML',
      ];
    case 'Social Media Icon':
      return ['Facebook', 'Google', 'Instagram', 'Linkedin', 'Twitter'];
    default:
      return [];
  }
};
